import { HELP_LOCALES, type HelpLocale } from './help-header-i18n';

/**
 * Rutas públicas del centro de ayuda, sin dependencias de servidor: las usan
 * la página del artículo (servidor) y el renderizador del markdown (cliente).
 *
 * En el CMS cada artículo se guarda como `<slug>-<idioma>` (el slug es único),
 * pero la URL pública lo esconde: `/es/help/subir-documento` → fila
 * `subir-documento-es`. Las traducciones de un mismo artículo comparten
 * `translationGroup`, aunque sus slugs sean distintos («subir-documento» y
 * «upload-document»).
 */
export interface HelpArticleRef {
  slug: string;
  language: string;
  translationGroup: string | null;
}

const LOCALE_SUFFIX = /-(es|en|pt|de|fr)$/;

export function stripLocaleSuffix(slug: string): string {
  return slug.replace(LOCALE_SUFFIX, '');
}

export function toHelpLocale(value: string | null | undefined): HelpLocale | null {
  const code = (value ?? '').toLowerCase().slice(0, 2);
  return (HELP_LOCALES as readonly string[]).includes(code) ? (code as HelpLocale) : null;
}

/** Idioma de una fila; `es` es el valor por defecto de la columna en la BD. */
export function articleLocale(article: Pick<HelpArticleRef, 'language'>): HelpLocale {
  return toHelpLocale(article.language) ?? 'es';
}

export function helpArticlePath(article: Pick<HelpArticleRef, 'slug' | 'language'>): string {
  return `/${articleLocale(article)}/help/${stripLocaleSuffix(article.slug)}`;
}

export type HelpResolution<T extends HelpArticleRef> =
  | { kind: 'serve'; article: T }
  | { kind: 'redirect'; from: T; target: T; href: string }
  | { kind: 'notFound' };

/**
 * Decide qué artículo sirve `/{locale}/help/{uiSlug}`.
 *
 * 1. Uno en el idioma de la URL: `<slug>-<locale>`, el slug exacto o
 *    cualquiera con el mismo slug base.
 * 2. Si solo existe en otro idioma (un enlace viejo, el «Relacionado» de
 *    Novedades o un `/help/…` de otro idioma), se busca su hermano por
 *    `translationGroup` y se redirige a él.
 * 3. Sin hermano en ese idioma, se sirve el que hay en vez de dar un 404;
 *    su canonical apunta a su propio idioma.
 *
 * La redirección no puede encadenarse: su destino está en el idioma de la URL
 * y el paso 1 lo sirve directamente.
 */
export function resolveHelpArticle<T extends HelpArticleRef>(
  articles: readonly T[],
  uiSlug: string,
  locale: HelpLocale,
): HelpResolution<T> {
  const bare = stripLocaleSuffix(uiSlug);
  const exact = articles.filter((a) => a.slug === uiSlug);
  const sameBase = articles.filter((a) => a.slug !== uiSlug && stripLocaleSuffix(a.slug) === bare);
  const candidates = [...exact, ...sameBase];
  if (candidates.length === 0) return { kind: 'notFound' };

  const inLocale = (a: T) => toHelpLocale(a.language) === locale;
  // Mismo orden que el resolver anterior: primero `<slug>-<locale>`.
  const direct =
    candidates.find((a) => a.slug === `${bare}-${locale}`) ??
    exact.find(inLocale) ??
    candidates.find(inLocale);
  if (direct) return { kind: 'serve', article: direct };

  for (const from of candidates) {
    if (!from.translationGroup) continue;
    const target = articles.find(
      (a) => a.translationGroup === from.translationGroup && inLocale(a),
    );
    if (target) {
      return {
        kind: 'redirect',
        from,
        target,
        href: `/${locale}/help/${stripLocaleSuffix(target.slug)}`,
      };
    }
  }
  return { kind: 'serve', article: candidates[0] };
}

const OWN_ORIGIN = /^https?:\/\/(?:www\.|staging\.|qa\.)?ezdocu\.ai(?=[/?#]|$)/i;

/**
 * Los artículos del CMS enlazan `/help/<slug>` sin idioma; sin prefijo, el
 * proxy elegía el idioma por la cookie y el enlace podía acabar en otro idioma
 * o en un 404. Se antepone el idioma de la página y el resolver hace el resto
 * (si el slug es de otro idioma, redirige a su traducción). Los enlaces
 * absolutos a nuestro dominio se vuelven relativos para no saltar de entorno.
 */
export function localizeHelpHref(href: string | undefined, locale: HelpLocale): string | undefined {
  if (!href) return href;
  const origin = OWN_ORIGIN.exec(href);
  const path = origin ? href.slice(origin[0].length) || '/' : href;
  if (/^\/help(?=[/?#]|$)/.test(path)) return `/${locale}${path}`;
  return href;
}

/**
 * Canonical y hreflang de un artículo: un `languages` por cada traducción
 * publicada del mismo `translationGroup`, y `x-default` en inglés si existe.
 * Rutas relativas: el `metadataBase` del layout raíz las hace absolutas.
 */
export function helpArticleAlternates(
  article: HelpArticleRef,
  articles: readonly HelpArticleRef[],
): { canonical: string; languages?: Record<string, string> } {
  const canonical = helpArticlePath(article);
  const byLocale = new Map<HelpLocale, string>([[articleLocale(article), canonical]]);
  if (article.translationGroup) {
    for (const a of articles) {
      if (a.translationGroup !== article.translationGroup) continue;
      const l = toHelpLocale(a.language);
      if (!l || byLocale.has(l)) continue;
      byLocale.set(l, helpArticlePath(a));
    }
  }
  if (byLocale.size < 2) return { canonical };
  const languages: Record<string, string> = {};
  for (const l of HELP_LOCALES) {
    const url = byLocale.get(l);
    if (url) languages[l] = url;
  }
  languages['x-default'] = byLocale.get('en') ?? canonical;
  return { canonical, languages };
}
